'use client'

import { useRef } from 'react'
import { Button, Image, Spacer, Text } from '@vinted/web-ui'
import { InView } from 'react-intersection-observer'

import { PromotionalBannerModel } from '@marketplace-web/banners/banners-data'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

type Props = {
  banner: PromotionalBannerModel
}

type TextType = React.ComponentProps<typeof Text>['type']
type ButtonStyling = React.ComponentProps<typeof Button>['styling']
type ButtonType = React.ComponentProps<typeof Button>['type']

const ReferralsPromotingBanner = ({ banner }: Props) => {
  const breakpoints = useBreakpoint()
  const abTest = useAbTest(banner.abTest.name || '')
  const trackExpose = useTrackAbTestCallback()
  const wasExposed = useRef(false)

  const foregroundImage = breakpoints.phones
    ? banner.foreground.darkTheme.imageUrl
    : banner.foreground.darkTheme.wideImageUrl

  const handleInViewChange = (inView: boolean) => {
    if (!inView) return
    if (!wasExposed.current) {
      trackExpose(abTest)
      wasExposed.current = true
    }
  }

  const renderContent = () => {
    if (banner.abTest.group?.toLowerCase() === 'off') {
      return null
    }

    return (
      <div
        className="referrals-promoting-banner__container"
        style={{
          background: banner.background.lightTheme.color,
        }}
      >
        <div className="u-flexbox">
          <div className="referrals-promoting-banner__text-content">
            <Text
              as="span"
              text={banner.title.text}
              theme={banner.title.lightTheme.textStyle}
              type={banner.title.lightTheme.textType as TextType}
            />
            <Spacer size="small" />
            <span className="referrals-promoting-banner__text-content-link">
              <Text
                as="span"
                text={banner.body.text}
                theme={banner.body.lightTheme.textStyle}
                type={banner.body.lightTheme.textType as TextType}
                html
              />
            </span>
            <Spacer size="medium" />
            <Button
              styling={banner.actions.primary.lightTheme.style as ButtonStyling}
              theme={banner.actions.primary.lightTheme.theme}
              type={banner.actions.primary.lightTheme.type as ButtonType}
              size="small"
              text={banner.actions.primary.title}
              url={banner.actions.primary.action.target}
              inline
            />
          </div>
          <div className="referrals-promoting-banner__img-content">
            <Image src={foregroundImage} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <InView onChange={handleInViewChange} threshold={0.5}>
      {renderContent()}
    </InView>
  )
}

export default ReferralsPromotingBanner
